import React, { useState, useEffect, useReducer } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Button,
  Avatar,
  TextField,
  InputAdornment,
  IconButton,
  styled
} from "@mui/material";
import {
  WhatsApp as WhatsAppIcon,
  Search as SearchIcon,
  DeleteOutline as DeleteOutlineIcon,
  Edit as EditIcon
} from "@mui/icons-material";

// Componentes do CallCenter
import MainLayout from "../../../components/Layout/MainLayout";
import Header from "../../../components/Header";
import MainContainer from "../../../components/MainContainer";
import MainHeader from "../../../components/MainHeader";
import Title from "../../../components/Title";
import MainHeaderButtonsWrapper from "../../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../../components/TableRowSkeleton";
import ContactModal from "../../../components/ContactModal";
import ConfirmationModal from "../../../components/ConfirmationModal";
import NewTicketModal from "../../../components/NewTicketModal";
import useAuth from "../../../hooks/useAuth";


// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(1),
  overflowY: "auto",
  overflowX: "auto",
  maxHeight: "calc(100vh - 200px)",
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5),
    maxHeight: "calc(100vh - 150px)",
  }
}));

const SearchField = styled(TextField)(({ theme }) => ({
  marginRight: theme.spacing(2),
  minWidth: 200,
  maxWidth: '100%',
  width: '100%',
  [theme.breakpoints.down('md')]: {
    minWidth: 150,
    marginRight: theme.spacing(1),
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: 0,
    marginRight: 0,
    marginBottom: theme.spacing(1),
    width: '100%',
  }
}));

// Reducer para gerenciar estado dos contatos
const contactsReducer = (state, action) => {
  switch (action.type) {
    case "LOAD_CONTACTS":
      const contacts = action.payload;
      const newContacts = [];

      contacts.forEach((contact) => {
        const contactIndex = state.findIndex((c) => c.id === contact.id);
        if (contactIndex !== -1) {
          state[contactIndex] = contact;
        } else {
          newContacts.push(contact);
        }
      });

      return [...state, ...newContacts];

    case "UPDATE_CONTACTS":
      const contact = action.payload;
      const contactIndex = state.findIndex((c) => c.id === contact.id);

      if (contactIndex !== -1) {
        state[contactIndex] = contact;
        return [...state];
      } else {
        return [contact, ...state];
      }

    case "DELETE_CONTACT":
      const contactId = action.payload;
      const index = state.findIndex((c) => c.id === contactId);
      if (index !== -1) {
        state.splice(index, 1);
      }
      return [...state];

    case "RESET":
      return [];

    default:
      return state;
  }
};

const Contacts = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const permissions = user?.permissions || user?.user?.permissions || [];
  const canDelete = Array.isArray(permissions) && permissions.includes('contacts.edit');

  // Estados
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(contactsReducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const fetchContacts = async (term, page) => {
    try {
      const { contacts = [] } = await (await import('../../../services/contactsService')).default.getContacts({
        searchParam: term || '',
        page: page || 1,
        limit: 50
      });
      dispatch({ type: "LOAD_CONTACTS", payload: contacts });
      setHasMore(false);
    } catch (err) {
      console.error("Erro ao buscar contatos:", err);
      dispatch({ type: "RESET" });
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  // Efeitos
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchContacts(searchParam, pageNumber);
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
  }, []);

  // Handlers
  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      navigate(`/tickets/${ticket.uuid}`);
    }
  };

  const handleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      const contactsService = (await import('../../../services/contactsService')).default;
      await contactsService.deleteContact(contactId);
      dispatch({ type: "DELETE_CONTACT", payload: contactId });
      toast.success("Contato excluído com sucesso!");
    } catch (err) {
      console.error("Erro ao excluir contato:", err);
      toast.error("Erro ao excluir contato");
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleImportContact = async (file) => {
    try {
      if (!file) return;
      const contactsService = (await import('../../../services/contactsService')).default;
      await contactsService.importContacts(file);
      toast.success("Contatos importados com sucesso!");
      dispatch({ type: "RESET" });
      setPageNumber(1);
      fetchContacts(searchParam, 1);
    } catch (err) {
      console.error("Erro ao importar contatos:", err);
      toast.error("Erro ao importar contatos");
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const exportContacts = async () => {
    try {
      const contactsService = (await import('../../../services/contactsService')).default;
      const blob = await contactsService.exportContacts({ searchParam: searchParam || '' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "contatos.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Erro ao exportar contatos:", err);
      toast.error("Erro ao exportar contatos");
    }
  };

  return (
    <MainLayout>
      <Header />
      <MainContainer>
        <NewTicketModal
          modalOpen={newTicketModalOpen}
          initialContact={contactTicket}
          onClose={handleCloseOrOpenTicket}
        />
        <ContactModal
          open={contactModalOpen}
          onClose={handleCloseContactModal}
          contactId={selectedContactId}
        />
        <ConfirmationModal
          title={deletingContact ? `Excluir contato ${deletingContact.name}?` : "Excluir contato?"}
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={() => deletingContact ? handleDeleteContact(deletingContact.id) : null}
        >
          {deletingContact ? "Tem certeza que deseja excluir este contato?" : ""}
        </ConfirmationModal>
        
        <MainHeader>
          <Title>Contatos</Title>
          <MainHeaderButtonsWrapper>
            <SearchField
              placeholder="Buscar contatos..."
              type="search"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={() => document.getElementById('contacts-import-input')?.click()}
              sx={{ mr: 1 }}
            >
              Importar
            </Button>
            <input
              id="contacts-import-input"
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files && e.target.files[0];
                e.target.value = '';
                handleImportContact(file);
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenContactModal}
              sx={{ mr: 1 }}
            >
              Adicionar
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={exportContacts}
            >
              Exportar
            </Button>
          </MainHeaderButtonsWrapper>
        </MainHeader>
        
        <StyledPaper variant="outlined" onScroll={handleScroll}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Nome</TableCell>
                <TableCell align="center">WhatsApp</TableCell>
                <TableCell align="center">Email</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contacts.map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell sx={{ paddingRight: 0 }}>
                    <Avatar src={contact.profilePicUrl} />
                  </TableCell>
                  <TableCell>{contact.name}</TableCell>
                  <TableCell align="center">{contact.number}</TableCell>
                  <TableCell align="center">{contact.email}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setContactTicket(contact);
                        setNewTicketModalOpen(true);
                      }}
                      title="Criar ticket"
                    >
                      <WhatsAppIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleEditContact(contact.id)}
                      title="Editar contato"
                    >
                      <EditIcon />
                    </IconButton>
                    {canDelete && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setConfirmOpen(true);
                          setDeletingContact(contact);
                        }}
                        title="Excluir contato"
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton avatar columns={3} />}
            </TableBody>
          </Table>
        </StyledPaper>

        <NewTicketModal
          modalOpen={newTicketModalOpen}
          initialContact={contactTicket}
          onClose={() => {
            setNewTicketModalOpen(false);
            setContactTicket(null);
          }}
          onCreated={() => {
            setNewTicketModalOpen(false);
            setContactTicket(null);
            navigate('/integrated-whatsapp-chat');
          }}
        />
      </MainContainer>
    </MainLayout>
  );
};

export default Contacts;
